import { useEffect, useState } from 'react';
import moment from 'moment';
import { EPagination, ETable } from 'libs/ui';
import { FilePenLine } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { TChargeUserType } from 'types';
import ESwitch from 'libs/ui/src/Switch/ESwitch';
import { usePagination } from 'hook/usePagination';
import { useFlightMarkupsData } from 'libs/data-access';
import { humanizeText, showToast } from 'utils/utility';
import ETableTooltipData from 'libs/ui/src/table/ETableTooltipData';

import { IFlightMarkupList } from '../Hooks/types';

interface IProps {
    userChargeType: TChargeUserType;
};

export default function FlightMarkList({ userChargeType }: IProps) {

    const navigate = useNavigate();

    const {
        getFlightMarkupsData,
        patchFlightMarkupsData
    } = useFlightMarkupsData();


    const [isLoadingIndex, setIsLoadingIndex] = useState<number | undefined>(undefined);
    const [paginationQueryParams, setPaginationQueryParams] = useState<{
        id: number | null;
        date: string | null;
    }>({
        id: null,
        date: null
    });
    const [sortingDetails, setSortingDetails] = useState<{
        sortBy: "id" | "createdDate";
        sortingOrder: "ASC" | "DESC";
    }>({
        sortBy: "createdDate",
        sortingOrder: "DESC"
    });


    // API CALLS
    const updateStatus = async (id: number, status: string, index: number) => {
        setIsLoadingIndex(index);

        const response = await patchFlightMarkupsData(`/${id}/${status}`, {});

        if (response.isSuccess) {
            showToast({
                type: "success",
                message: "Markup status updated successfully",
            });
            setDataList(prev => prev.map(
                (each, i) =>
                    i === index
                        ?
                        { ...each, status: status === "active" ? true : false }
                        :
                        each
            ));
        } else {
            showToast({
                type: "error",
                message: response.message ?? "Something went wrong",
            })
        };

        setIsLoadingIndex(undefined);
    };


    const {
        dataList: flightMarkupList,
        setDataList,
        isLoading,
        setSearchText,
        searchText,
        goToNextPage,
        goToPreviousPage,
        disablePrev,
        disableNext,
        initialLoading,
        setTriggerNextPage,
        setTriggerPreviousPage,
        goToInitialPage
    } = usePagination<IFlightMarkupList>({
        fetchListData: getFlightMarkupsData,
        maxLimit: 5,
        // sortby: sortingDetails?.sortBy,
        sortingOrder: sortingDetails?.sortingOrder,
        extraQueryParams: {
            userType: userChargeType
        } as Record<string, string | number | boolean>,
        paginationParams: paginationQueryParams as Record<string, string | number | boolean>,
        dependencies: []
    });


    return (
        <div>
            <ETable loading={isLoading}>
                <thead>
                    <tr>
                        <th>Markup Type</th>
                        <th>Apply For</th>
                        <th>Date Range</th>
                        <th>Price Range</th>
                        <th>Flight Class</th>
                        <th>Flight Cabin</th>
                        <th>Origin Country</th>
                        <th>Destination Country</th>
                        <th>Origin Airport</th>
                        <th>Destination Airport</th>
                        <th>Charges</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {
                        flightMarkupList.map((each, index) => (
                            <tr key={index + "flight-markup-list"}>
                                <td>
                                    {each.flightTypes?.length ? each.flightTypes?.map((each) => each.key).join(", ") : "All"}
                                </td>
                                <td>
                                    {each.flightChargeApplyFor?.key}
                                </td>
                                <td>
                                    {each.startDate ? moment(each.startDate).format("DD-MM-YYYY") : ""} - {each.endDate ? moment(each.endDate).format("DD-MM-YYYY") : ""}
                                </td>
                                <td>
                                    {each.minimumGrossPrice || ""} - {each.maximumGrossPrice || ""}
                                </td>
                                <td>
                                    {each.flightClass?.length ? ETableTooltipData(each.flightClass?.map((each) => each.key)) : "All"}
                                </td>
                                <td>
                                    {each.flightCabin?.length ? ETableTooltipData(each.flightCabin?.map((each) => each.key)) : "All"}
                                </td>
                                <td>
                                    {each.originCountryResponses?.length ? ETableTooltipData(each.originCountryResponses?.map((each) => each.name)) : "All"}
                                </td>
                                <td>
                                    {each.destinationCountryResponses?.length ? ETableTooltipData(each.destinationCountryResponses?.map((each) => each.name)) : "All"}
                                </td>
                                <td>
                                    {each.originAirportResponses?.length ? ETableTooltipData(each.originAirportResponses?.map((each) => each.name)) : "All"}
                                </td>
                                <td>
                                    {each.destinationAirportResponses?.length ? ETableTooltipData(each.destinationAirportResponses?.map((each) => each.name)) : "All"}
                                </td>
                                <td>
                                    {each.markupChargeWithAmountTypes?.map((each, index) => (
                                        <div key={index + "markup-charge-with-amount-type-responses"}>
                                            {`${each.markupFormCharge.key}: ${each.amount} - ${humanizeText(each.feeType)}`}
                                        </div>
                                    ))}
                                </td>
                                <td>
                                    <ESwitch
                                        label={each.status ? "Active" : "Inactive"}
                                        loading={isLoadingIndex === index}
                                        checked={each.status}
                                        onChange={(value) => updateStatus(each.id, value ? "active" : "inActive", index)}
                                    />
                                </td>
                                <td>
                                    <FilePenLine
                                        className="cursor-pointer text-green-500 hover:bg-green-200 transition-all duration-300 rounded-full p-1"
                                        onClick={() => {
                                            navigate(`/admin-transactions/markup_and_commission/markup/b2c/flight/${userChargeType}?mno=${each.id}`);
                                        }}
                                    />
                                </td>
                            </tr>
                        ))
                    }
                </tbody>
            </ETable>

            <EPagination
                isLoading={isLoading}
                disablePrev={disablePrev}
                disableNext={disableNext}
                goToNextPage={() => {
                    // setPaginationQueryParams({
                    //   ...paginationQueryParams,
                    //   date: hotelMarkupList[hotelMarkupList.length - 1].bookingDate
                    // });
                    setTriggerNextPage(true);
                }}
                goToPreviousPage={() => {
                    // setPaginationQueryParams({
                    //   ...paginationQueryParams,
                    //   date: hotelMarkupList[0].bookingDate
                    // });
                    setTriggerPreviousPage(true);
                }}
            />
        </div>
    );

}
